import { Matrix4, type BufferGeometry } from 'three';
import type { LinearRGB } from '../../core/color';
import type { Vec2, Vec3 } from '../../geo/types';
import { layoutToWorld } from '../../core/coords';
import {
  createMeshBuilder,
  type Frame,
  type MeshBuilder,
  type VertexStyle,
} from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import { BEAM_Y_BOTTOM, type PalisadeData, type PalisadeSpan } from './palisade';
import { loftSolid, type Ring3 } from './shipSolids';
import { SHIP_PAINTER_SCALE_H, SHIP_PAINTER_SCALE_V } from './shipPlacement';
import { nearestRow, onRow } from './rowSnap';

/*
 * The painting stands a small timber house on the inner pile row, beside an opening in it (scan
 * 4336-4388 x 3778-3873, the opening 4410-4455 along the row); the woodcut draws the same house at
 * the end of a row beside an opening, two floors of square windows in it (woodcut 2440-2470 x
 * 3025-3070), and Wyngaerde two such houses at openings between the rows (560,420-1400,480).
 * Measured on the scan and drawn at the painter scale of the ships about it (docs/plans/ships.md
 * 11): 3.5 m square, eaves 5.8 m and ridge 7.1 m above the water, two floors, the ridge along
 * the row, whose gable ends the painter marks with a finial each.
 */
export const BOOM_HOUSE = {
  plan: 3.5,
  floor: BEAM_Y_BOTTOM,
  eaves: 5.8,
  ridge: 7.1,
} as const;

export const BOOM_HOUSE_PAINTER_SCALE: readonly [number, number] = [
  SHIP_PAINTER_SCALE_H,
  SHIP_PAINTER_SCALE_V,
];

/* The woodcut's windows: two floors of two, square, in the wall it shows. */
const WINDOW = { half: 0.25, xs: [-0.8, 0.8], ys: [1.9, 4.5], sunk: 0.02, proud: 0.03 } as const;
const CORNER_PILE_INSET = 0.2;
const LOCAL: Frame = { origin: [0, 0, 0], yaw: 0 };
/** A house standing further than this from every row is not on the palisade the layout has. */
export const MAX_BOOM_SNAP_M = 20;

export interface BoomHouseData {
  readonly id: string;
  /** Layout ground under the painted house's base. */
  readonly house: Vec2;
  /** Layout ground under the painted opening's two ends. */
  readonly opening: readonly [Vec2, Vec2];
}

/* Scan pixels through the painter camera and the committed residual warp to the ground
   (tools/layout/painterframe.painted_to_ground, 2026-09-27): the house's base 4362,3873 and the
   opening's ends 4410,3845 and 4455,3825. */
export const BOOM_HOUSES: readonly BoomHouseData[] = [
  {
    id: 'boom-house-inner-row',
    house: [480.6, 542.6],
    opening: [
      [471.1, 546.2],
      [463.4, 550.5],
    ],
  },
];

export interface PlacedBoomHouse {
  readonly id: string;
  readonly palisadeId: string;
  readonly position: Vec2;
  /** Metres along its palisade's path. */
  readonly station: number;
  /** World turn about +Y that lays the ridge along the row and the windowed wall toward the IJ. */
  readonly turn: number;
  readonly opening: PalisadeSpan;
}

/* Local +X runs along the row and local +Z to the right of it, (ty, -tx) in the layout; turned
   half round where that points south, so the windowed wall faces north, to the IJ. */
function turnOf([tx, ty]: Vec2): number {
  const turn = Math.atan2(ty, tx);
  return -tx < 0 ? turn + Math.PI : turn;
}

/** Each house on the row nearest the ground under it, its opening measured along that row. */
export function placeBoomHouses(
  houses: readonly BoomHouseData[],
  palisades: readonly PalisadeData[],
  warn: (message: string) => void
): readonly PlacedBoomHouse[] {
  return houses.flatMap((house) => {
    const nearest = nearestRow(palisades, house.house);
    if (!nearest || nearest.distance > MAX_BOOM_SNAP_M) {
      warn(`Boom house ${house.id} stands off every palisade`);
      return [];
    }
    const ends = house.opening.map((p) => onRow(nearest.palisade, p).station);
    return [
      {
        id: house.id,
        palisadeId: nearest.palisade.id,
        position: nearest.point,
        station: nearest.station,
        turn: turnOf(nearest.tangent),
        opening: { from: Math.min(...ends), to: Math.max(...ends) },
      },
    ];
  });
}

/** The instance matrix: origin on the water at the centre of the plan. */
export function boomHouseMatrix(house: PlacedBoomHouse): Matrix4 {
  const [x, y, z] = layoutToWorld(house.position, 0);
  return new Matrix4().makeRotationY(house.turn).setPosition(x, y, z);
}

/** Layout points of the piles under the house's corners. */
export function boomHousePiles(house: PlacedBoomHouse): readonly Vec2[] {
  const reach = BOOM_HOUSE.plan / 2 - CORNER_PILE_INSET;
  const [c, s] = [Math.cos(house.turn), Math.sin(house.turn)];
  const [x0, y0] = house.position;
  return [-1, 1].flatMap((i) =>
    [-1, 1].map((k): Vec2 => {
      const [lx, lz] = [i * reach, k * reach];
      return [x0 + lx * c + lz * s, y0 + lx * s - lz * c];
    })
  );
}

function style(color: LinearRGB, edgeId: number, seed: number): VertexStyle {
  return { color, surface: SURFACE.wood, edgeId, seed, strokeAngle: 0, extent: [0, 0] };
}

/* A wall section across the ridge, swept along +X: the sweep crossed with the ring's tangent
   points out of the solid (shipSolids.loftSolid). */
function section(x: number): Ring3 {
  const { plan, floor, eaves, ridge } = BOOM_HOUSE;
  const h = plan / 2;
  return [
    [x, floor, h],
    [x, eaves, h],
    [x, ridge, 0],
    [x, eaves, -h],
    [x, floor, -h],
  ];
}

function buildWindows(builder: MeshBuilder, windowStyle: VertexStyle): void {
  const face = BOOM_HOUSE.plan / 2;
  const { half, xs, ys, sunk, proud } = WINDOW;
  for (const x of xs) {
    for (const y of ys) {
      const min: Vec3 = [x - half, y - half, face - sunk];
      const max: Vec3 = [x + half, y + half, face + proud];
      builder.box(windowStyle, LOCAL, min, max);
    }
  }
}

/** The house in its own frame: ridge along +X, windowed wall toward +Z, origin on the water. */
export function buildBoomHouse(
  wood: LinearRGB,
  window: LinearRGB,
  edgeId: number,
  seed: number
): BufferGeometry {
  const builder = createMeshBuilder();
  const h = BOOM_HOUSE.plan / 2;
  loftSolid(builder, style(wood, edgeId, seed), [section(-h), section(h)]);
  buildWindows(builder, style(window, edgeId, seed));
  return builder.build();
}
